import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, Input } from '../../../ui';
import { ClusterShop } from '../hooks/useClusterShops';
import { Chain } from '../hooks/useChains';

const SHOP_TYPES = [
    { value: 'independent', label: 'Independent' },
    { value: 'coffee-chain', label: 'Chain' },
    { value: 'roastery', label: 'Roastery' },
    { value: 'bakery-cafe', label: 'Bakery & Cafe' },
    { value: 'hotel-cafe', label: 'Hotel Cafe' },
    { value: 'vending-machine', label: 'Vending Machine' },
    { value: 'self-made', label: 'Self-made' },
    { value: 'other', label: 'Other' }
];

interface ShopEditModalProps {
    open: boolean;
    onClose: () => void;
    shop: ClusterShop | null;
    chains: Chain[];
    onSave: (id: string, data: { shopType?: string; chainId?: string | null; name?: string; address?: string; city?: string; country?: string }) => Promise<void>;
}

const ShopEditModal: React.FC<ShopEditModalProps> = ({ open, onClose, shop, chains, onSave }) => {
    const [shopType, setShopType] = useState(shop?.shopType || 'independent');
    const [chainId, setChainId] = useState<string | null>(shop?.chainId || null);
    const [name, setName] = useState(shop?.name || '');
    const [address, setAddress] = useState(shop?.address || '');
    const [city, setCity] = useState(shop?.city || '');
    const [country, setCountry] = useState(shop?.country || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (shop) {
            setShopType(shop.shopType || 'independent');
            setChainId(shop.chainId || null);
            setName(shop.name || '');
            setAddress(shop.address || '');
            setCity(shop.city || '');
            setCountry(shop.country || '');
        }
    }, [shop]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!shop) return;
        setSaving(true);
        try {
            await onSave(shop._id, {
                shopType,
                chainId: chainId === '' ? null : (chainId || undefined),
                name: name.trim(),
                address: address.trim(),
                city: city.trim(),
                country: country.trim()
            });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    if (!shop) return null;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit shop: {shop.name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">Name</label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Shop name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">Shop type</label>
                        <select
                            value={shopType}
                            onChange={(e) => setShopType(e.target.value)}
                            className="w-full px-3 py-2 border border-ink/20 rounded-lg bg-paper text-ink"
                        >
                            {SHOP_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">Chain</label>
                        <select
                            value={chainId || ''}
                            onChange={(e) => setChainId(e.target.value || null)}
                            className="w-full px-3 py-2 border border-ink/20 rounded-lg bg-paper text-ink"
                        >
                            <option value="">None</option>
                            {chains.map((c) => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">Address</label>
                        <Input
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Address"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">City</label>
                            <Input
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="City"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1">Country</label>
                            <Input
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder="Country"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ShopEditModal;
